import React from "react";
import PropTypes from "prop-types";
import { Box, IconButton, useMediaQuery } from "@mui/material";
import {
    LeaderboardOutlined,
    GroupAddOutlined,
    GroupsOutlined,
    PlayArrowRounded,
    LoginOutlined,
    PersonSearchOutlined,
    SettingsOutlined,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import Tooltip from "../common/Tooltip";
import StatusDot from "../common/StatusDot";
import { markShellIntroComplete, shouldPlayShellIntro } from "./shellIntroMotion";
import { alpha } from "@mui/material/styles";
import { useHistory, useLocation } from "react-router-dom";
import { useWebSocketStore } from "../../stores/websocketStore";
import {
    APP_SHELL_TOP_OFFSET_SX,
    MOBILE_NAV_HEIGHT,
    MOBILE_NAV_MEDIA,
    NAV_SIDEBAR_HEIGHT_SX,
} from "./layoutConstants";
import {
    isLeaderboardV2Path,
    isLobbyV2Path,
    toLeaderboardPath,
    toLobbyPath,
} from "../../universal/v2Routes";
import {
    closeLobbySettingsPanel,
    openLobbySettingsPanel,
    useLobbySettingsPanel,
} from "../../stores/lobbySettingsPanelStore";

const NAV_ITEMS = [
    { id: "lobby", label: "Lobby", tooltip: "Play", Icon: PlayArrowRounded },
    { id: "friendlist", label: "Friendlist", tooltip: "Friendlist", Icon: GroupsOutlined },
    { id: "createParty", label: "Create Party", tooltip: "Create party", Icon: GroupAddOutlined },
    { id: "joinParty", label: "Join Party", tooltip: "Join party", Icon: LoginOutlined },
    { id: "leaderboard", label: "Leaderboard", tooltip: "Leaderboard", Icon: LeaderboardOutlined },
    { id: "findPlayer", label: "Search", tooltip: "Search players", Icon: PersonSearchOutlined },
    {
        id: "matchmakingSetup",
        label: "Matchmaking setup",
        tooltip: "Matchmaking setup",
        Icon: SettingsOutlined,
    },
];

const MOBILE_NAV_ITEM_IDS = [
    "lobby",
    "friendlist",
    "findPlayer",
    "leaderboard",
    "matchmakingSetup",
];

const NAV_ITEMS_BY_ID = Object.fromEntries(NAV_ITEMS.map((item) => [item.id, item]));

const MOBILE_NAV_ITEMS = MOBILE_NAV_ITEM_IDS.map((id) => NAV_ITEMS_BY_ID[id]);

const NAV_SELECT_TRANSITION = "opacity 0.15s ease-in-out, color 0.15s ease-in-out";
const MotionBox = motion(Box);

function resolveNavSelection(pathname) {
    if (pathname === "/" || pathname === "/lobby" || isLobbyV2Path(pathname)) return "lobby";
    if (pathname === "/leaderboard" || isLeaderboardV2Path(pathname)) return "leaderboard";
    return null;
}

export default function NavSidebar({
    actions,
    catalog,
    globalSearchOpen = false,
    onGlobalSearchOpen,
    onGlobalSearchClose,
}) {
    const history = useHistory();
    const location = useLocation();
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);
    const { sendOwnerAction } = useWebSocketStore();
    const { open: matchmakingSetupOpen } = useLobbySettingsPanel();
    const selected = globalSearchOpen
        ? "findPlayer"
        : matchmakingSetupOpen
          ? "matchmakingSetup"
          : resolveNavSelection(location.pathname);
    const playShellIntro = shouldPlayShellIntro();
    const navItems = isMobile ? MOBILE_NAV_ITEMS : NAV_ITEMS;

    React.useEffect(() => {
        if (playShellIntro) {
            markShellIntroComplete();
        }
    }, [playShellIntro]);

    const accountStatusRaw = catalog?.user?.accountStatus;
    const accountStatus = typeof accountStatusRaw === "string"
        ? accountStatusRaw.trim().toLowerCase()
        : null;
    const isBanned = accountStatus === "banned";
    const isInParty = Boolean(catalog?.currentLobbyCode);

    const { friends } = catalog;
    let friendsOnlineCount = 0;
    friends?.forEach((friend) => {
        if (friend?.friendData?.isOnline) friendsOnlineCount++;
    });

    return (
        <>
        <MotionBox
            aria-label="Lobby navigation"
            initial={
                playShellIntro
                    ? (isMobile ? { y: 36, opacity: 0 } : { x: -36, opacity: 0 })
                    : false
            }
            animate={
                isMobile
                    ? { y: 0, opacity: 1 }
                    : { x: 0, opacity: 1 }
            }
            transition={{
                duration: 0.55,
                ease: [0.22, 1, 0.36, 1],
            }}
            sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                border: `1px solid ${colors.border.subtle}`,
                borderRadius: "1rem",
                background: alpha(colors.background.dark, 0.3),
                backdropFilter: "blur(10px)",
                boxSizing: "border-box",
                boxShadow: `
                    0 10px 34px rgba(0,0,0,0.34),
                    inset 0 1px 0 rgba(255,255,255,0.03)
                `,
                overflow: "hidden",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(255,255,255,0.03) 0%,
                            rgba(255,255,255,0.01) 100%
                        )
                    `,
                },
                "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "1px",
                    background: `
                        linear-gradient(
                            90deg,
                            transparent,
                            rgba(232,163,61,0.18),
                            transparent
                        )
                    `,
                },
                ...(isMobile
                    ? {
                        position: "fixed",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: "100%",
                        height: `calc(${MOBILE_NAV_HEIGHT} + env(safe-area-inset-bottom, 0px))`,
                        pb: "env(safe-area-inset-bottom, 0px)",
                        zIndex: globalSearchOpen ? 1510 : 1300,
                        flexDirection: "row",
                        justifyContent: "space-around",
                        gap: 0,
                        px: "0.5rem",
                        pt: 0,
                        borderRadius: "1rem 1rem 0 0",
                        background: `
                            linear-gradient(
                                180deg,
                                #0D0D10 0%,
                                ${colors.background.dark} 100%
                            )
                        `,
                        backdropFilter: "none",
                    }
                    : {
                        width: { xs: 68, xl: 78 },
                        position: "sticky",
                        top: APP_SHELL_TOP_OFFSET_SX,
                        height: NAV_SIDEBAR_HEIGHT_SX,
                        flexDirection: "column",
                        gap: "0.7rem",
                        py: "0.85rem",
                        zIndex: globalSearchOpen ? 1510 : "auto",
                    }),
            }}
        >
            {navItems.map(({ id, label, tooltip, Icon }) => {
                const isSelected = selected === id;
                const isPartyAction = id === "createParty" || id === "joinParty";
                const isDisabled =
                    (isBanned && id === "createParty") ||
                    (isInParty && isPartyAction);
                const itemTooltip =
                    isInParty && isPartyAction
                        ? "Leave your party first"
                        : isBanned && id === "createParty"
                          ? "Unavailable while banned"
                          : tooltip;

                return (
                    <Box
                        key={id}
                        id={id}
                        sx={{
                            position: "relative",
                            width: isMobile ? "auto" : "100%",
                            flex: isMobile ? 1 : undefined,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                opacity: isSelected ? 1 : 0,
                                transition: NAV_SELECT_TRANSITION,
                                pointerEvents: "none",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: "50%",
                                    top: "50%",
                                    width: { xs: "2.45rem", xl: "2.75rem" },
                                    height: { xs: "2.45rem", xl: "2.75rem" },
                                    marginLeft: { xs: "calc(-2.45rem / 2)", xl: "calc(-2.75rem / 2)" },
                                    marginTop: { xs: "calc(-2.45rem / 2)", xl: "calc(-2.75rem / 2)" },
                                    borderRadius: "0.65rem",
                                    background: colors.accent.subtle,
                                    zIndex: 0,
                                }}
                            />
                            <Box
                                sx={
                                    isMobile
                                        ? {
                                            position: "absolute",
                                            top: 0,
                                            left: "calc(50% - 0.6rem)",
                                            width: "1.2rem",
                                            height: "2px",
                                            borderRadius: "0 0 2px 2px",
                                            background: colors.accent.main,
                                            boxShadow: `0 0 8px 1px ${alpha(colors.accent.main, 0.4)}`,
                                            zIndex: 1,
                                        }
                                        : {
                                            position: "absolute",
                                            left: 0,
                                            top: "calc(50% - 0.6rem)",
                                            width: "2px",
                                            height: "1.2rem",
                                            borderRadius: "0 2px 2px 0",
                                            background: colors.accent.main,
                                            boxShadow: `0 0 8px 1px ${alpha(colors.accent.main, 0.4)}`,
                                            zIndex: 1,
                                        }
                                }
                            />
                        </Box>

                        <Tooltip title={itemTooltip} placement={isMobile ? "top" : "right"}>
                            <Box sx={{ position: "relative", zIndex: 2 }}>
                                <IconButton
                                    aria-label={label}
                                    aria-current={isSelected ? "page" : undefined}
                                    disabled={isDisabled}
                                    disableRipple
                                    onClick={async () => {
                                        if (isDisabled) return;
                                        if (id !== "findPlayer" && globalSearchOpen) {
                                            onGlobalSearchClose?.();
                                        }
                                        if (id === "friendlist") {
                                            closeLobbySettingsPanel();
                                            actions?.setFriendSidebarOpen?.(true);
                                            return;
                                        }
                                        if (id === "matchmakingSetup") {
                                            actions?.setFriendSidebarOpen?.(false);
                                            if (matchmakingSetupOpen) {
                                                closeLobbySettingsPanel();
                                            } else {
                                                openLobbySettingsPanel();
                                            }
                                            return;
                                        }
                                        if (id === "lobby") {
                                            closeLobbySettingsPanel();
                                            history.push(toLobbyPath(location.pathname));
                                            return;
                                        }
                                        if (id === "joinParty") {
                                            closeLobbySettingsPanel();
                                            actions?.openJoinLobbyDialog();
                                            return;
                                        }
                                        if (id === "leaderboard") {
                                            closeLobbySettingsPanel();
                                            history.push(toLeaderboardPath(location.pathname));
                                            return;
                                        }
                                        if (id === "findPlayer") {
                                            closeLobbySettingsPanel();
                                            onGlobalSearchOpen?.();
                                            return;
                                        }
                                        if (id === "createParty") {
                                            closeLobbySettingsPanel();
                                            history.push(toLobbyPath(location.pathname));
                                            const userId = catalog?.user?.userId || catalog?.user?._id || null;
                                            if (userId && typeof sendOwnerAction === "function") {
                                                try {
                                                    await sendOwnerAction({
                                                        messageId: "create-solo-lobby",
                                                        userId,
                                                    });
                                                } catch (e) {
                                                    console.error("NavSidebar: create-solo-lobby failed", e);
                                                }
                                            }
                                        }
                                    }}
                                    sx={{
                                        width: { xs: "2.45rem", xl: "2.75rem" },
                                        height: { xs: "2.45rem", xl: "2.75rem" },
                                        borderRadius: "0.65rem",
                                        backgroundColor: "transparent",
                                        color: isDisabled
                                            ? colors.text.secondary
                                            : isSelected
                                                ? colors.accent.main
                                                : colors.text.primary,
                                        opacity: isDisabled ? 0.42 : 1,
                                        transition: NAV_SELECT_TRANSITION,
                                        "&:hover": {
                                            backgroundColor: isDisabled
                                                ? undefined
                                                : isSelected
                                                    ? "transparent"
                                                    : colors.accent.subtle,
                                            ...(!isDisabled && {
                                                color: colors.accent.main,
                                            }),
                                        },
                                    }}
                                >
                                    <Icon sx={{ fontSize: { xs: "1.35rem", xl: "1.55rem" } }} />
                                    {id === "friendlist" ? (
                                        <StatusDot
                                            className="onlineIndicator"
                                            online={friendsOnlineCount > 0}
                                            variant="avatar"
                                            animateGlow={false}
                                        />
                                    ) : null}
                                </IconButton>
                            </Box>
                        </Tooltip>
                    </Box>
                );
            })}

        </MotionBox>

        </>
    );
}

NavSidebar.propTypes = {
    actions: PropTypes.object,
    catalog: PropTypes.object,
    globalSearchOpen: PropTypes.bool,
    onGlobalSearchOpen: PropTypes.func,
    onGlobalSearchClose: PropTypes.func,
};
